import { Box, useMediaQuery } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Option } from '@spike/model';
import { onboardingShowVideo } from 'actions/onboardingSteps/OnboardingStepsActions';
import { ONBOARDING_VIDEOS } from 'components/OnboardingSteps/videos';
import OnboardingTutorial from 'components/OnboardingTutorial';
import { ResultNotFound } from 'components/Services/ResultsNotFound';
import { Spinner } from 'components/UI/Spinner';
import { OnboardingSteps } from 'model/OnboardingSteps';
import Staff from 'model/Staff';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import React, { Fragment, FunctionComponent, useEffect, useState } from 'react';
import { Events } from 'analytics/events';
import { isDefault, isCustomized } from 'components/StaffSchedule/utils/StaffScheduleUtils';
import useOneShotMarketplaceEvent from 'hooks/useOneShotMarketplaceEvent';
import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { StaffStatus } from 'reducers/staff/StaffState';
import { RootState } from 'store';
import { StaffList } from './StaffList';
import { HeaderSearchable } from 'components/UI/HeaderSearchable/HeaderSearchable';
import { resendInvitationThunk } from 'actions/staff/StaffActions';
import { staffSlides } from './Slides';

interface Props {
    staff: Array<Staff>;
    editStaff: (staffMemberId: number) => void;
    deleteStaff: (member: Staff) => void;
    addStaff: () => void;
    activate: (member: Staff) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#FFF'
        },
        header: {
            marginBottom: '30px'
        },

        selectorBarContainer: {
            display: 'flex',
            marginLeft: '100px',
            marginBottom: '20px',
            [theme.breakpoints.down('lg')]: {
                marginLeft: '5%'
            }
        },
        resultNotFound: {
            height: '60vh'
        },
        onboarding: {
            paddingTop: '0px',
            marginTop: '-85px',
            [theme.breakpoints.down('sm')]: {
                marginTop: '-40px'
            },
            [theme.breakpoints.down('md')]: {
                marginTop: '-40px'
            }
        }
    })
);

export const StaffListComponent: FunctionComponent<Props> = props => {
    const classes = useStyles();

    const dispatch = useDispatch();
    const staffStatus = useSelector<RootState, StaffStatus>(state => state.staff.status);
    const onboardingSteps = useSelector<RootState, OnboardingSteps>(state => state.onboardingSteps.onboardingSteps);

    const [staffList, setStaffList] = useState(props.staff);
    const [textSearch, setTextSearch] = useState('');
    const [statusPills, setStatusPills] = useState('active');

    useEffect(() => {
        setStaffList(props.staff.filter(item => item.active));

        onSelectFilter(statusPills);
        // eslint-disable-next-line
    }, [props.staff]);

    const allOption: Option<string> = {
        id: 'all',
        name: 'All'
    };

    const activeOption: Option<string> = {
        id: 'active',
        name: 'Active'
    };

    const inactiveOption: Option<string> = {
        id: 'inactive',
        name: 'Inactive'
    };

    const filterOptions: Array<Option<string>> = [activeOption, inactiveOption, allOption];

    const handleSearchMemberName = (text: string): void => {
        setTextSearch(text);

        if (statusPills === 'inactive') {
            const staffListAux = props.staff.filter(item => {
                const name = `${item.person.firstName.toLowerCase()} ${item.person.lastName.toLowerCase()}`;
                return !item.active && (text.length === 0 || name.indexOf(text.toLowerCase()) > -1);
            });
            setStaffList(staffListAux);
        } else if (statusPills === 'active') {
            const staffListAux = props.staff.filter(item => {
                const name = `${item.person.firstName.toLowerCase()} ${item.person.lastName.toLowerCase()}`;
                return item.active && (text.length === 0 || name.indexOf(text.toLowerCase()) > -1);
            });
            setStaffList(staffListAux);
        } else {
            const staffListAux = props.staff.filter(item => {
                const name = `${item.person.firstName.toLowerCase()} ${item.person.lastName.toLowerCase()}`;
                return text.length === 0 || name.indexOf(text.toLowerCase()) > -1;
            });
            setStaffList(staffListAux);
        }
    };

    const onSelectFilter = (id: string) => {
        setStatusPills(id);
        if (id === 'inactive') {
            const staffListAux = props.staff.filter(item => {
                const name = `${item.person.firstName.toLowerCase()} ${item.person.lastName.toLowerCase()}`;
                return !item.active && (textSearch.length === 0 || name.indexOf(textSearch.toLowerCase()) > -1);
            });
            setStaffList(staffListAux);
        } else if (id === 'active') {
            const staffListAux = props.staff.filter(item => {
                const name = `${item.person.firstName.toLowerCase()} ${item.person.lastName.toLowerCase()}`;
                return item.active && (textSearch.length === 0 || name.indexOf(textSearch.toLowerCase()) > -1);
            });
            setStaffList(staffListAux);
        } else {
            const staffListAux = props.staff.filter(item => {
                const name = `${item.person.firstName.toLowerCase()} ${item.person.lastName.toLowerCase()}`;
                return textSearch.length === 0 || name.indexOf(textSearch.toLowerCase()) > -1;
            });
            setStaffList(staffListAux);
        }
    };

    const handleActivate = (activateStaffarr: Staff) => {
        onSelectFilter(statusPills);
        props.activate(activateStaffarr);
    };

    const handleEdit = (staffId: number) => {
        props.editStaff(staffId);
    };

    const handleResendInvitation = (staffId: number) => {
        dispatch(resendInvitationThunk(staffId));
    };

    const onboardingPageParams = {
        imageWidth: '100%',
        imageHeight: 'auto'
    };

    const onboarding = (
        <OnboardingTutorial
            imageWidth={onboardingPageParams.imageWidth}
            imageHeight={onboardingPageParams.imageHeight}
            onPressLinkToVideo={() => {
                dispatch(onboardingShowVideo(ONBOARDING_VIDEOS.TEAM_SCHEDULE));
            }}
            className={classes.onboarding}
            slides={staffSlides}
        />
    );

    // Marketplace id for one-shot tracking keys
    const marketplaceId = useSelector((state: RootState) => state.marketplace.marketplace?.id);
    const staffState = useSelector((state: RootState) => state.staff);
    const onboardingStepsState = useSelector((state: RootState) => state.onboardingSteps.onboardingSteps);

    // First staff member saved (basic profile) while onboarding incomplete
    const staffSavedFirstTimeCondition =
        !onboardingStepsState.teamDone && staffState.status === StaffStatus.SaveSuccess && !!staffState.savedStaff?.id;
    useOneShotMarketplaceEvent(
        Events.STAFF_MEMBER_SAVED,
        staffSavedFirstTimeCondition,
        {
            staff_id: staffState.savedStaff?.id,
            staff_role: staffState.savedStaff?.role?.id
        },
        { gateOnboardingTeamIncomplete: true }
    );

    // First schedule configuration (either at member save or explicit default schedule save)
    const scheduleConfigured = (() => {
        if (!staffState.savedStaff?.schedule) return false;
        const schedule = staffState.savedStaff.schedule;
        const hasDefaultDays = isDefault(schedule) || Object.values(schedule.default).some(d => d !== null);
        const hasCustom = isCustomized(schedule) || schedule.customDays.length > 0;
        return hasDefaultDays || hasCustom;
    })();
    const staffScheduleFirstTimeCondition =
        !onboardingStepsState.teamDone &&
        scheduleConfigured &&
        (staffState.status === StaffStatus.SaveDefaultScheduleSuccess || staffState.status === StaffStatus.SaveSuccess);
    const defaultDaysConfiguredCount = staffState.savedStaff?.schedule
        ? Object.values(staffState.savedStaff.schedule.default).filter(v => v !== null).length
        : undefined;
    useOneShotMarketplaceEvent(
        Events.STAFF_SCHEDULE_SAVED,
        staffScheduleFirstTimeCondition,
        {
            staff_id: staffState.savedStaff?.id,
            default_days_configured: defaultDaysConfiguredCount,
            has_custom_days: !!staffState.savedStaff?.schedule?.customDays?.length
        },
        { gateOnboardingTeamIncomplete: true }
    );

    return (
        <Box className={classes.container}>
            <OverlayScrollbarsComponent style={{ height: '100%' }}>
                <HeaderSearchable
                    title="Staff"
                    button={{
                        label: 'Add Member',
                        id: 'staff_button_add_member',
                        onClick: () => {
                            if (!onboardingStepsState.teamDone) {
                                // Manual one-shot using localStorage to avoid hook call inside handler lifecycle.
                                try {
                                    const key = `event_one_shot_${Events.ADD_STAFF_MEMBER_CLICKED}_${marketplaceId}`;
                                    if (marketplaceId && localStorage.getItem(key) !== '1') {
                                        localStorage.setItem(key, '1');
                                        // Fire event
                                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                        import('analytics/client').then(({ default: Analytics }) => {
                                            Analytics.track(Events.ADD_STAFF_MEMBER_CLICKED as any, {
                                                marketplace_id: marketplaceId
                                            });
                                        });
                                    }
                                } catch (_) {
                                    // non-blocking
                                }
                            }
                            props.addStaff();
                        }
                    }}
                    filter={{
                        selectedId: statusPills,
                        options: filterOptions,
                        onSelect: onSelectFilter
                    }}
                    search={{
                        onSearch: handleSearchMemberName,
                        placeholder: 'Search by member name',
                        id: 'staff_filter_search_by_member'
                    }}
                />

                <Box style={{ width: '100%' }}>
                    {staffList.length > 0 && (
                        <StaffList
                            staff={staffList}
                            editStaff={handleEdit}
                            deleteStaff={props.deleteStaff}
                            activate={handleActivate}
                            sortedStaff={sortedStaff => setStaffList(sortedStaff)}
                            resendInvitation={handleResendInvitation}
                        />
                    )}
                    {staffList.length === 0 && onboardingSteps.teamDone && (
                        <Box className={classes.resultNotFound}>
                            {staffStatus !== StaffStatus.Fetching ? (
                                <ResultNotFound
                                    title={<Fragment>Nothing to see here</Fragment>}
                                    message={
                                        <Fragment>
                                            There are currently no{' '}
                                            {statusPills === 'active'
                                                ? 'active'
                                                : statusPills === 'inactive'
                                                ? 'inactive'
                                                : ''}{' '}
                                            staff members.
                                            <br />
                                            Just keep Cuddling!
                                        </Fragment>
                                    }
                                />
                            ) : (
                                <Spinner />
                            )}
                        </Box>
                    )}
                    {!onboardingSteps.teamDone && onboarding}
                </Box>
            </OverlayScrollbarsComponent>
        </Box>
    );
};

export default StaffListComponent;
